(function(global){
  // Visualizer.Helpers.Url module: URL param application and persistence
  function _normalizeDoorOpeningKey(key) {
    if (!key || typeof key !== 'string') return key
    if (key === 'doors-simpleOpeningDirection') return 'doors-openingDirection'
    const m = key.match(/^doors-simpleOpeningDirection-(\d+)$/)
    if (m) return `doors-openingDirection-${m[1]}`
    return key
  }
  // Local helper: normalize CSS color strings to 6-char HEX (#RRGGBB) when possible
  function toHexColor(input) {
    if (!input || typeof input !== 'string') return input
    const color = input.trim()
    // Hex formats
    if (color.startsWith('#')) {
      let hex = color.slice(1)
      if (hex.length === 3) hex = hex.split('').map(c => c + c).join('')
      if (hex.length === 6) return '#' + hex.toUpperCase()
      return color
    }
    // rgb/rgba formats
    const match = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/i)
    if (match) {
      const r = Math.max(0, Math.min(255, parseInt(match[1], 10)))
      const g = Math.max(0, Math.min(255, parseInt(match[2], 10)))
      const b = Math.max(0, Math.min(255, parseInt(match[3], 10)))
      const hex = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase()
      return hex
    }
    return color
  }
  function isKnownVisualizerParamKey(key) {
    if (!key || typeof key !== 'string') return false
    const prefixes = [
      'columnTemplate-',
      'columnShelvesCount-',
      'columnDrawersCount-',
      'columnDividerHeight-',
      'shelvesCount-',
      'doors-openingDirection-',
      'doors-simpleOpeningDirection-'
    ]
    return prefixes.some((p) => key === p || key.startsWith(p))
  }

  // Normalize image src into a stable key (just the filename, lowercase)
  function normalizeImageSrc(src) {
    try {
      const u = new URL(src, window.location.origin)
      const pathname = u.pathname || ''
      const clean = pathname.split('/').pop() || pathname
      return (clean || String(src)).split('?')[0].toLowerCase()
    } catch (e) {
      const noQuery = String(src).split('?')[0]
      return noQuery.substring(noQuery.lastIndexOf('/') + 1).toLowerCase()
    }
  }

  const SKIP_KEY_PREFIXES = ['__']
  const SKIP_EXACT_KEYS = new Set([
    'columnVisibility'
  ])

  function _isVisualizerParam(key, opts = {}) {
    if (!key || typeof key !== 'string') return false
    if (opts.excludeInternal && key.startsWith('__')) return false
    const prefixes = ['columnTemplate-', 'columnShelvesCount-', 'columnDrawersCount-', 'columnDividerHeight-', 'shelvesCount-', 'doors-openingDirection-', 'doors-simpleOpeningDirection-', 'dimensions-']
    if (prefixes.some((p) => key === p || key.startsWith(p))) return true
    const exact = new Set(['doors-type', 'countertop-enabled', 'color', 'columnsCount', 'materialRange', 'luxeColor', 'shelvesCount', 'doors-style', 'doorsColor', 'doorsLuxeColor', 'doorsMaterialRange', 'doorsMaterialDifferent', 'edge-color', 'edge-color-yes-no', 'rail-width', 'stile-width', 'top-hinge-offset', 'bottom-hinge-offset', 'hinges-count', 'runner-length', 'runner-amount', 'mountType', 'baseType-type', 'baseType-legCode', 'baseType-color', 'baseType-plinth-height', 'handlesAdd', 'handles-color', 'handles-code', 'countertop-overhang', 'faceFrame', 'countertopColor', 'adjustable', 'cable-hole-position', 'cut-out', 'cut-out-height', 'cut-out-depth', '__supportsConfigurableBorder'])
    return exact.has(key) || (!opts.excludeInternal && key.startsWith('__'))
  }

  function _buildVisualizerQueryFromSearch(paramsSource) {
    const source = paramsSource instanceof URLSearchParams
      ? paramsSource
      : new URLSearchParams(paramsSource || window.location.search)
    const params = new URLSearchParams()
    for (const [key, value] of source.entries()) {
      if (key === 'variant') continue
      if (!_isVisualizerParam(key, { excludeInternal: true })) continue
      if (value === undefined || value === null || value === '') continue
      params.set(key, value)
    }
    return params.toString()
  }

  function _templateHasDrawers(template) {
    if (!template || typeof template !== 'string') return false
    const upper = template.toUpperCase()
    return (
      upper === 'T1' ||
      upper === 'T2' ||
      upper === 'T5' ||
      upper === 'T6' ||
      upper === 'T7' ||
      upper.startsWith('T1:') ||
      upper.startsWith('T2:') ||
      upper.startsWith('T5:') ||
      upper.startsWith('T6:') ||
      upper.startsWith('T7:')
    )
  }
  function _templateHasDoors(template) {
    if (!template || typeof template !== 'string') return false
    const upper = template.toUpperCase()
    if (upper.includes(':HASDOOR')) return true
    if (upper.includes(':NODOOR')) return false
    return (
      upper === 'T4' ||
      upper === 'T6' ||
      upper === 'T7' ||
      upper.startsWith('T4:') ||
      upper.startsWith('T6:') ||
      upper.startsWith('T7:')
    )
  }

  function _sanitizeForUrl(data, configType = 'classic') {
    const out = {}
    const isWallUnit = configType === 'wall-unit'
    let columnsCount = Math.max(0, parseInt(data['columnsCount'] || data['ColumnsCount'] || 0, 10) || 0)
    // Infer from max column index if columnsCount not set
    if (columnsCount === 0) {
      let maxIdx = 0
      for (let k in data) {
        const m = k.match(/^(?:columnTemplate|columnShelvesCount|doors-openingDirection)-(\d+)$/)
        if (m) maxIdx = Math.max(maxIdx, parseInt(m[1], 10))
      }
      if (maxIdx > 0) columnsCount = maxIdx
    }

    for (let key in data) {
      let val = data[key]

      if (isWallUnit) {
        if (key === 'drawersCount') continue
        if (key.startsWith('columnDrawersCount-')) continue
      }

      if (val === undefined || val === null || val === '') continue

      if (val === 'null') continue

      if (typeof val === 'object' && !Array.isArray(val)) continue

      if (SKIP_KEY_PREFIXES.some((p) => key.startsWith(p))) continue
      if (SKIP_EXACT_KEYS.has(key)) continue

      if (key === 'faceFrame' && val === '') continue

      if (key === 'countertopColor' && (val === 'null' || !val)) continue

      const colMatch = key.match(/^(columnTemplate|columnShelvesCount|columnDrawersCount|columnDividerHeight|shelvesCount|doors-openingDirection)-(\d+)$/)
      if (colMatch) {
        const index = parseInt(colMatch[2], 10)
        if (index < 1 || index > columnsCount) continue

        if (isWallUnit && key.startsWith('columnTemplate-') && _templateHasDrawers(String(val))) continue

        if (key.startsWith('shelvesCount-') && data[`columnShelvesCount-${index}`] != null) continue

        if (key.startsWith('columnDrawersCount-')) {
          const template = data[`columnTemplate-${index}`]
          if (!_templateHasDrawers(template)) continue
        }
        if (key.startsWith('doors-openingDirection-')) {
          const template = data[`columnTemplate-${index}`]
          if (!_templateHasDoors(template)) continue
        }
      }

      if (key === 'doorsMaterialRange' && data['doorsMaterialDifferent'] === 'same') continue

      if (key === 'edge-color' && (data['edge-color-yes-no'] === 'no' || data['edge-color-yes-no'] === false)) continue

      if ((key === 'rail-width' || key === 'stile-width') && data['__supportsConfigurableBorder'] !== true) continue

      if (Array.isArray(val)) {
        const filtered = val.filter((v) => v !== undefined && v !== null && v !== '' && v !== 'null')
        if (filtered.length === 0) continue
        out[key] = filtered.join(',')
      } else {
        out[key] = String(val)
      }
    }

    for (let i = 1; i <= columnsCount; i += 1) {
      const template = data[`columnTemplate-${i}`]
      const drawersKey = `columnDrawersCount-${i}`
      const openingKey = `doors-openingDirection-${i}`
      if (_templateHasDrawers(template) && (out[drawersKey] == null || out[drawersKey] === '')) {
        const drawersVal = data[drawersKey]
        if (drawersVal !== undefined && drawersVal !== null && drawersVal !== '' && drawersVal !== 'null') {
          out[drawersKey] = String(drawersVal)
        }
      }
      if (!_templateHasDoors(template) && out[openingKey] != null) {
        delete out[openingKey]
      }
    }

    return out
  }

  const Url = {
    isVisualizerParam(key, opts) {
      return _isVisualizerParam(key, opts)
    },

    buildVisualizerQueryFromSearch(paramsSource) {
      return _buildVisualizerQueryFromSearch(paramsSource)
    },

    // Apply URLSearchParams to config and sync to inputs in the DOM
    getFromUrl(config, inputs, paramsSource) {
      const params = paramsSource instanceof URLSearchParams ? paramsSource : new URLSearchParams(window.location.search)
      const V = (typeof window !== 'undefined' && window.Visualizer) ? window.Visualizer : {}
      const configType = (V && typeof V.getConfigType === 'function') ? V.getConfigType() : (V?.isDoor ? 'door' : 'classic')
      const isWallUnit = configType === 'wall-unit'
      for (let [key, value] of params.entries()) {
        if (isWallUnit) {
          if (key === 'drawersCount' || key.startsWith('columnDrawersCount-')) continue
          if (key.startsWith('columnTemplate-') && _templateHasDrawers(value)) continue
        }
        const normalizedKey = _normalizeDoorOpeningKey(key)
        const hasKey = Object.prototype.hasOwnProperty.call(config, key)
        const hasNormalized = normalizedKey && Object.prototype.hasOwnProperty.call(config, normalizedKey)
        const hasInputKey = Array.isArray(inputs) && inputs.some((i) => i && i.name === key)
        const hasInputNormalized = normalizedKey && Array.isArray(inputs) && inputs.some((i) => i && i.name === normalizedKey)
        const targetKey = hasKey
          ? key
          : (hasNormalized
            ? normalizedKey
            : (hasInputKey
              ? key
              : (hasInputNormalized ? normalizedKey : null)))
        const keyToSet = targetKey || (isKnownVisualizerParamKey(key) ? key : (isKnownVisualizerParamKey(normalizedKey) ? normalizedKey : null))
        if (keyToSet && value) {
          // Preserve rgb/rgba color values which contain commas; don't split them into arrays
          if (/^rgba?\(/i.test(value)) {
            config[keyToSet] = value
          } else {
            const parts = value.split(',').filter(Boolean)
            config[keyToSet] = parts.length > 1 ? parts : parts[0]
          }
        }
      }
      for (let key in config) {
        const value = config[key]
        const inputList = inputs.filter(item => item.name === key)
        if (inputList.length === 0) {
          if (isKnownVisualizerParamKey(key)) {
            try {
              const container = document.querySelector('.dpo-container')
              const el = container ? container.querySelector(`[visualizer="${key}"]`) : document.querySelector(`[visualizer="${key}"]`)
              if (el) {
                const strVal = value != null ? (Array.isArray(value) ? value.join(',') : String(value)) : ''
                if (el.classList.contains('input-text') || el.classList.contains('dpo-slider-value')) {
                  if (el.value !== strVal) {
                    el.value = strVal
                    el.dispatchEvent(new Event('change', { bubbles: true }))
                  }
                } else if (el.classList.contains('options-list')) {
                  const change = new Event('change', { bubbles: true })
                  const valStr = value != null ? String(value) : ''
                  const matches = Array.from(el.querySelectorAll('.admin__field-option')).filter((wrapper) => {
                    const inputEl = wrapper.querySelector('input[type="radio"], input[type="checkbox"]')
                    if (inputEl && inputEl.value != null) return (Array.isArray(value) ? value.map(String).includes(inputEl.value) : inputEl.value === valStr)
                    return false
                  })
                  matches.forEach((item) => {
                    const inputEl = item.querySelector('input[type="radio"], input[type="checkbox"]')
                    if (inputEl && !inputEl.checked) {
                      inputEl.checked = true
                      inputEl.dispatchEvent(change)
                    }
                  })
                }
              }
            } catch (e) { /* non-fatal */ }
          }
          continue
        }
        const change = new Event('change', { bubbles: true })
        // assign value to cached input states
        inputList.forEach(item => item.value = value)
        // text inputs
        if (inputList.some(item => item.node.classList.contains('input-text'))) {
          const textInputs = inputList.filter(item => item.node.classList.contains('input-text'))
          textInputs.forEach(item => {
            if (item.node.value !== value) {
              item.node.value = value
              item.node.dispatchEvent(change)
            }
          })
        }
        // slider inputs (stored via hidden text field with .dpo-slider-value)
        else if (inputList.some(item => item.node.classList.contains('dpo-slider-value'))) {
          const sliderInputs = inputList.filter(item => item.node.classList.contains('input-text'))
          sliderInputs.forEach(item => {
            if (item.node.value !== value) {
              item.node.value = value
              item.node.dispatchEvent(change)
            }
          })
        }
        // options lists (radio/checkbox groups)
        else if (inputList.some(item => item.node.classList.contains('options-list'))) {
          inputList
            .filter(item => item.node.classList.contains('options-list'))
            .forEach(input => {
              let matches = Array.from(input.node.querySelectorAll('.admin__field-option'))
                .filter(wrapper => {
                  let isCurrent = false

                  if (wrapper.querySelector('.dpo-choice-none') && value === 'null') {
                    return true
                  }

                  if (value === 'null') { return false }

                  // 1) Match by class token data-visualizer-value-*
                  let keyValue = Array.from(wrapper.classList).find(k => k.startsWith('data-visualizer-value-'))
                  if (keyValue) {
                    keyValue = keyValue.replace(/^data-visualizer-value-/, '')
                    isCurrent = Array.isArray(value) ? value.includes(keyValue) : keyValue === value
                  }

                  // 2) Match by image src thumbnail
                  if (!isCurrent) {
                    const img = wrapper.querySelector('.itoris-dynamicoptions-thumbnail-image')
                    if (img) {
                      const imgKey = normalizeImageSrc(img.src)
                      const values = Array.isArray(value) ? value : [value]
                      isCurrent = values.some(v => normalizeImageSrc(v) === imgKey)
                    }
                  }

                  // 3) Match by color swatch (supports hex vs rgb equivalence)
                  if (!isCurrent) {
                    const colorEl = wrapper.querySelector('.itoris-dynamicoptions-thumbnail-color')
                    if (colorEl) {
                      // Prefer explicit data attribute or inline style when element is hidden
                      const dataColor = colorEl.getAttribute('data-color') || colorEl.dataset?.color
                      const inlineColor = colorEl.style && colorEl.style.backgroundColor
                      // Fallback to computed style
                      const computedColor = window.getComputedStyle(colorEl).getPropertyValue('background-color')
                      const candidates = [dataColor, inlineColor, computedColor].filter(Boolean)

                      const values = Array.isArray(value) ? value : [value]
                      isCurrent = candidates.some((targetVal) => {
                        return values.some((v) => {
                          const a = toHexColor(v)
                          const b = toHexColor(targetVal)
                          if (typeof a === 'string' && typeof b === 'string' && a.startsWith('#') && b.startsWith('#')) {
                            return a.toUpperCase() === b.toUpperCase()
                          }
                          // Fallback to strict string compare
                          return (v + '').trim() === (targetVal + '').trim()
                        })
                      })
                    }
                  }

                  // 4) Match by the underlying input's value attribute (robust when visual markers are hidden)
                  if (!isCurrent) {
                    const inputEl = wrapper.querySelector('input[type="radio"], input[type="checkbox"]')
                    if (inputEl && inputEl.value != null) {
                      const v = inputEl.value
                      isCurrent = Array.isArray(value) ? value.includes(v) : v === value
                    }
                  }

                  return isCurrent
                })
              const updates = []
              matches.forEach((item) => {
                let inputEl = item.querySelector('input[type=radio]')
                if (inputEl && !inputEl.checked) { inputEl.checked = true; updates.push(inputEl) }
                inputEl = item.querySelector('input[type=checkbox]')
                if (inputEl && !inputEl.checked) { inputEl.checked = true; updates.push(inputEl) }
              })
              updates.forEach((el) => el.dispatchEvent(change))
              try { config[input.name] = value } catch(e) {}
            })
        }
      }
    },

    // Wait for defaults to be present in DOM, then apply params over them
    async setFromUrl(opts) {
      const { hasIncomingParams, originalParams, config, inputs } = opts || {}
      await new Promise((res) => {
        const container = document.querySelector('.dpo-container')
        if (!container) return res(true)

        const maxWait = 6000
        const idleMs = 100
        let idleTimer = null

        const hasDefaultsInDom = () => {
          const hasOptions = container.querySelectorAll('.options-list .admin__field-option').length > 0
          const someChecked = container.querySelectorAll('.options-list input[type="radio"]:checked, .options-list input[type="checkbox"]:checked').length > 0
          const sliders = Array.from(container.querySelectorAll('.dpo-slider-value'))
          const slidersReady = sliders.length === 0 || sliders.every(inp => inp.value != null && inp.value !== '')
          return hasOptions && (someChecked || slidersReady)
        }

        if (hasDefaultsInDom()) {
          idleTimer = setTimeout(() => res(true), idleMs)
          return
        }

        const observer = new MutationObserver(() => {
          if (!hasDefaultsInDom()) return
          clearTimeout(idleTimer)
          idleTimer = setTimeout(() => {
            observer.disconnect()
            res(true)
          }, idleMs)
        })
        observer.observe(container, { attributes: true, childList: true, subtree: true })
        setTimeout(() => { try { observer.disconnect() } catch(e){}; res(true) }, maxWait)
      })

      if (hasIncomingParams) {
        // Apply from original captured params with multiple retries to overcome late DPO defaults
        const applyOnce = () => { try { Url.getFromUrl(config, inputs, originalParams) } catch(e) {} }
        // Immediate apply
        applyOnce()
        // Staggered retries
        ;[300, 800, 1600, 2400].forEach((ms) => setTimeout(applyOnce, ms))
      } else {
        const applyDefault = () => { try { Url.getFromUrl(config, inputs) } catch(e) {} }
        try {
          let raw = (window.__tcsSavedUrl || '').toString().trim()
          if (!raw) {
            const meta = document.querySelector('meta[name="tcs-saved-url"]')
            if (meta && meta.content) raw = meta.content.trim()
          }
          let paramsFromSaved = null
          if (raw) {
            let q = raw
            const qm = q.indexOf('?')
            if (qm !== -1) q = q.slice(qm + 1)
            if (q.startsWith('?') || q.startsWith('&')) q = q.slice(1)
            if (q) paramsFromSaved = new URLSearchParams(q)
          }
          if (paramsFromSaved) {
            const applyOnce = () => { try { Url.getFromUrl(config, inputs, paramsFromSaved) } catch(e) {} }
            applyOnce()
            ;[300, 800, 1600, 2400].forEach((ms) => setTimeout(applyOnce, ms))
          } else {
            applyDefault()
          }
        } catch (e) {
          applyDefault()
        }
      }
    },

    // Save valid data values into URL (no iframe dependency)
    saveToUrl(data, Validation) {
      const V = (typeof window !== 'undefined' && window.Visualizer) ? window.Visualizer : {}
      const type = (V && typeof V.getConfigType === 'function') ? V.getConfigType() : (V?.isDoor ? 'door' : 'classic')
      const isValid = !!(Validation && Validation.isValidConfig && Validation.isValidConfig(data))
      const alwaysSaveDoor = new Set(['doors-style', 'rail-width', 'stile-width', 'top-hinge-offset', 'bottom-hinge-offset', 'hinges-count', 'cable-hole-position'])

      if (!isValid && type !== 'door') return

      const sanitized = _sanitizeForUrl(data, type)

      const url = new URL(window.location.href)
      const params = new URLSearchParams()
      for (const [key, val] of new URLSearchParams(url.search).entries()) {
        if (!_isVisualizerParam(key)) params.set(key, val)
      }
      const toApply = {}
      if (isValid) {
        for (let key in sanitized) toApply[key] = sanitized[key]
      } else if (type === 'door') {
        alwaysSaveDoor.forEach((key) => {
          const val = sanitized[key]
          if (val != null && val !== '') toApply[key] = val
        })
      }
      for (let key in toApply) params.set(key, toApply[key])
      url.search = params.toString()
      history.replaceState(data, '', url)
    }
  }

  global.Visualizer = global.Visualizer || {}
  global.Visualizer.Helpers = global.Visualizer.Helpers || {}
  global.Visualizer.Helpers.Url = Url
})(window);
